import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { genererCanauxRuban, MATCAP_GAIN, CHROME_BOX } from './lib/matcap.js';
import { vdjColor, vdjBeatChroma, VdjPost, VdjComfort } from './lib/vdjLook.js';

// Contrat du rig partagé : interdit. Les arcs sont la seule lumière.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'tesla',
  family: 'stage',
  danceability: 0.92,
  energyLevel: 0.88,
  bpmMin: 110,
  bpmMax: 175,
  movement: 0.80,
  tonality: 0.35,
  prefers: { percussiveness: [0.55, 1.0] },
  enabled: true
};

// Tesla : deux bobines Tesla géantes de part et d'autre de la pochette, dans
// une salle noire. Des arcs jaillissent de leurs tores en rythme, comme les
// bobines « chantantes » des concerts, et se rejoignent au-dessus de la
// pochette sur les temps forts. Techno, électro, bass music, industrial,
// dubstep. Règles de kiosk-app/CLAUDE.md R1 à R17 ; rythme « party » (R9 :
// danceability 0,92).
//
// L'ESPACE. Caméra à l'horizontale en (0, 0, CAM_D), regard vers -z, champ
// réglé pour que le plan z = 0 (celui des bobines) se lise directement en
// unités de HAUTEUR d'écran : un point (x, y, 0) tombe à (x, y) dans les
// coordonnées de la pochette (safeZone : la hauteur vaut 2). Les bobines, la
// pochette et les zones d'interface de R6 se comparent donc sans projection.
//
// QUATRE COUCHES, UN DRAW CALL CHACUNE, AUCUN UPLOAD D'ATTRIBUT PAR IMAGE :
//  1. LE SOL - un plan noir BRILLANT : le lobe flou des sources d'arc
//     (couche 3) s'y reflète sous chaque bobine, calculé par pixel (R12) ;
//     les bobines s'y reflètent aussi (leur maillage en miroir, assombri,
//     sans les boîtes nettes du matcap : un miroir analytique flou).
//  2. LES BOBINES - un seul maillage fusionné, construit en fractions de H
//     (hauteur d'une bobine : 55 % de l'écran), de bas en haut :
//       socle  (0 à 0,13 H) coffret noir mat à arêtes arrondies, quatre
//              pieds, fentes d'aération et deux voyants (rouge, vert) en
//              façade ; 6 entretoises blanches jusqu'à 0,18 H ;
//       plateforme (0,18 H) disque d'acrylique fumé, diamètre 0,55 H ;
//       primaire spirale d'Archimède conique (9 spires, montée de 20°) en
//              tube de cuivre, sur 4 peignes isolants ; anneau de garde en
//              cuivre au bord de la plateforme ;
//       secondaire (0,20 à 0,78 H) colonne fine (diamètre 0,12 H) bobinée
//              de fil émaillé : les spires sont dans la NORMALE (sinus en
//              hauteur, fondu par fwidth), embouts sombres ;
//       tore   (0,78 à 0,92 H) aluminium poli, diamètre 0,5 H, section
//              0,14 H, fermé par deux dômes ; pointe de décharge 0,04 H.
//     La droite porte un petit tore empilé sur le grand.
//     Matériaux (R5) : matcap CHROME de lib/matcap.js pour l'aluminium, le
//     même teinté cuivre (ombres brun-rouge, reflets chauds) pour le cuivre ;
//     socle noir que seuls les arcs éclairent. Normales analytiques (les
//     arêtes du coffret aussi : boîte arrondie construite par projection).
//     LES ARCS ÉCLAIRENT : les 4 arcs les plus forts sont 4 sources
//     ponctuelles (uniforms séparés, boucle déroulée : V3D) ; sur chaque
//     matériau  lumière += couleur x intensité x max(0, n.l) / (1 + (d/D)²)
//     plus un reflet spéculaire sur le métal.
//  3. LES ARCS - une ligne de 64 points instanciée : 16 emplacements d'arc
//     (6 par bobine + 4 ponts) x (1 tronc + 4 branches) = 80 instances. Le
//     vertex shader calcule chaque point par DÉPLACEMENT DE POINT MÉDIAN
//     (6 niveaux, amplitude x 0,55 par niveau) à partir de la graine de
//     l'emplacement (uniform) et de l'index du point, par hachage ; la graine
//     change toutes les 1/30 s : l'arc crépite, chaque image reste nette.
//     Fat-line en PIXELS (R1) : coeur blanc 2 px, gaine 6 px à luminance
//     0,7, halo exp(-d / 10) x 0,35 ramené à 0 au bord du ruban (6 largeurs
//     de halo, R12). Un arc part de la pointe ou d'un point du tore, long de
//     1,5 à 3 rayons de tore, avec 2 à 4 branches. Chaque point est POUSSÉ
//     hors de la boîte de la pochette (gonflée) : aucun arc ne passe devant.
//  4. LE REFLET DES ARCS - la même géométrie, en miroir sous le plan du sol,
//     sans coeur, élargie et atténuée avec la hauteur.
//
// LE RYTHME. Gauche = graves (bands.low), droite = aigus (bands.high) : une
// enveloppe par bobine, attaque instantanée, décroissance de 80 ms ; le
// nombre d'arcs actifs et leur longueur suivent l'enveloppe. TEMPS : la
// gauche décharge ; CONTRETEMPS : la droite ; MESURE : un pont au-dessus de
// la pochette ; PHRASE : couleur suivante (violet, bleu glacier, accent de la
// pochette) ; MONTÉE : arcs plus longs, crépitement jusqu'à 3 fois plus
// rapide ; DROP : gerbe de 12 arcs vers le haut, flash, ponts permanents
// pendant 2 mesures. Sans analyse (bands nuls) : graves synthétiques sur le
// temps, aigus sur le contretemps et les doubles-croches.
//
// CONFORT (R10). L'éclairage de la salle (sol, lueur des bobines) ne repart
// que si VdjComfort accorde un flash (3 par seconde au plus) ; les arcs
// eux-mêmes crépitent librement.

const TIER = {
  0: { maxArcs: 4, branches: 2, torus: [24, 72], spiral: 40, round: 3 },
  1: { maxArcs: 6, branches: 3, torus: [32, 96], spiral: 56, round: 4 },
  2: { maxArcs: 6, branches: 4, torus: [40, 128], spiral: 72, round: 5 }
};

// l'espace (unités de hauteur d'écran dans le plan z = 0)
const CAM_D = 3.6;
const FLOOR_Y = -0.76;
const H_FULL = 2 * 0.55;           // H : 55 % de la hauteur d'écran
const FRAME_MARGIN = 0.10;         // R6 : 5 % de la hauteur
// L'ANATOMIE, en fractions de H, origine au sol sous l'axe de la bobine
export const COIL = {
  footH: 0.02, footR: 0.018,
  boxTop: 0.13, boxW: 0.44, boxD: 0.36, boxR: 0.025,
  standR: 0.15, standW: 0.012,
  platY: 0.18, platT: 0.015, platR: 0.275,
  spiralR0: 0.09, spiralR1: 0.25, spiralTurns: 9, spiralRise: 20, spiralTube: 0.0068, spiralY0: 0.205,
  railR: 0.265, railT: 0.0065, railY: 0.222,
  secR: 0.06, secY0: 0.20, secY1: 0.78, capH: 0.03, capR: 0.068,
  torY: 0.85, torR: 0.18, torT: 0.07, domeH: 0.03,
  tipR: 0.005, tipTop: 0.96, ballR: 0.009,
  // la droite : un petit tore empilé sur le grand
  neckR: 0.045, tor2Y: 0.955, tor2R: 0.10, tor2T: 0.035, dome2H: 0.02, tip2Top: 1.035
};
const TURNS_PER_H = 145;           // spires du secondaire par H (~4 px à 1080p)
// les arcs
const SLOTS = 16, BRIDGE0 = 12, BRANCHES = 4;
const LINES = SLOTS * (1 + BRANCHES);
const POINTS = 64;
const HALO_SEGS = 8;                // le halo et le reflet suivent un tracé grossier (8 segments)
const AMP_DECAY = 0.55;
const TRUNK_HALO = 10, BRANCH_HALO = 5, MIRROR_HALO = 12;
const COVER_MARGIN = 0.05;         // la boîte de la pochette, gonflée
const ZONE_MARGIN = 0.03;
const ENV_DECAY = 0.080;           // s : l'intensité des arcs
const ACT_DECAY = 0.40;            // s : le NOMBRE d'arcs (un arc tient environ un temps)
const RESEED = 1 / 30;
const DROP_HOLD_BARS = 2;
// la lumière
const LIGHTS = 4;
const LIGHT_D = 0.45;              // la distance (unités) où la lumière d'un arc tombe de moitié
const LIGHT_GAIN = 1.6;
const ALU_REST = 0.42;             // l'aluminium au repos : le studio du matcap, sous le seuil
const COPPER_REST = 0.40;
const SAFE_FLOOR = 0.45;
const DROP_SCORE_MIN = 0.7, DROP_STALE_MS = 1500;
const FALLBACK_ACCENT = 0xff3bd0;
const VIOLET = new THREE.Color().setHSL(0.78, 0.95, 0.55);
const GLACIER = new THREE.Color().setHSL(0.55, 0.90, 0.60);
const CORE = [0.95, 0.90, 1.0];
const CORE_GAIN = 1.25;            // le coeur : seul élément au-dessus du seuil de bloom
const SHEATH_LUM = 0.70;

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  try { return new URLSearchParams(location.search).get(name); } catch (e) { return null; }
}
function lum(c) { return 0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b; }

// --------------------------------------------------------------- la borne
// Le déplacement de point médian ne dépasse jamais amp x MD_BOUND (somme des
// amplitudes des 6 niveaux, chaque décalage dans [-1, 1]) : c'est la marge
// que le CPU garde autour de la pochette quand il choisit une cible.
export const MD_BOUND = (1 - Math.pow(AMP_DECAY, 6)) / (1 - AMP_DECAY);
// ... et au point t : le niveau l n'y pèse que min(1, 2^l x distance au bout)
export function mdBoundAt(t) {
  const d = Math.min(t, 1 - t);
  let s = 0, a = 1;
  for (let l = 1; l <= 6; l++) { s += a * Math.min(1, Math.pow(2, l) * d); a *= AMP_DECAY; }
  return s;
}

// --------------------------------------------------------------- les shaders

const SAFE_CHUNK = `
  uniform float uSafeAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  float safeAtt(vec2 ndc) {
    vec2 s = vec2(ndc.x * uSafeAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
  }
`;

const ARC_VERTEX = `
  attribute float aLine;
  uniform vec4 uB[${SLOTS}];        // w : intensité
  uniform vec4 uD[${SLOTS}];        // branches, croissance, fondu de fin (arc libre)
  uniform vec2 uRes;            // la cible de rendu, en pixels
  uniform float uMirror;
  uniform float uHalo;          // 1 : la couche du halo (tracé grossier, large)
  uniform float uSegs;          // segments par ligne : 63 (coeur et gaine) ou 8 (halo, reflet)
  uniform float uBranchesOn;
  uniform sampler2D uPath;      // les tracés : xyz, et en w la racine d'une branche sur son tronc
  uniform float uSafeAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform vec2 uStageCenter;    // R19 : l'évitement se rapporte au centre de scène
  uniform vec2 uStageHalfSize;
  varying float vU;
  varying float vV;
  varying float vL;
  varying float vI;
  varying float vBranch;
  varying vec2 vNdc;

  // le point t d'une ligne : lu dans la texture des tracés (64 points par
  // ligne, calculés par le CPU à chaque crépitement), interpolé entre deux points
  vec4 pathAt(float row, float t) {
    float x = t * ${POINTS - 1}.0, k = min(floor(x), ${POINTS - 2}.0);
    float v = (row + 0.5) / ${LINES}.0;
    vec4 a = texture2D(uPath, vec2((k + 0.5) / ${POINTS}.0, v));
    vec4 b = texture2D(uPath, vec2((k + 1.5) / ${POINTS}.0, v));
    return mix(a, b, x - k);
  }
  // la boîte de la pochette (gonflée) : un point dedans est poussé vers le
  // bord le plus proche - en unités de hauteur, dans le clip
  vec4 pushOut(vec4 c) {
    vec2 h = vec2(c.x / c.w * uSafeAspect, c.y / c.w);
    vec2 d = h - uStageCenter;
    vec2 hs = uStageHalfSize + vec2(${COVER_MARGIN.toFixed(3)});
    vec2 q = abs(d) - hs;
    if (q.x < 0.0 && q.y < 0.0) {
      if (q.x > q.y) h.x = uStageCenter.x + sign(d.x) * hs.x;
      else h.y = uStageCenter.y + sign(d.y) * hs.y;
      c.xy = vec2(h.x / uSafeAspect, h.y) * c.w;
    }
    return c;
  }
  // la distance (unités de hauteur) d'un point du clip à la boîte gonflée
  float coverDist(vec4 c) {
    vec2 h = vec2(c.x / c.w * uSafeAspect, c.y / c.w);
    vec2 q = abs(h - uStageCenter) - uStageHalfSize - vec2(${COVER_MARGIN.toFixed(3)});
    return length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
  }
  vec3 pointAt(float row, float t) {
    vec3 p = pathAt(row, t).xyz;
    if (uMirror > 0.5) p.y = ${(2 * FLOOR_Y).toFixed(3)} - p.y;
    return p;
  }

  // l'intensité le long, au point tt (0 à l'origine, 1 au bout)
  float alongAt(float row, float b, float tt, float grow, float free) {
    float along = smoothstep(0.0, 0.04, tt);
    if (b > 0.5) {
      // une branche n'apparaît qu'une fois le tronc arrivé à sa racine
      float tb = pathAt(row, 0.0).w;
      along = smoothstep(0.0, 0.08, tt) * pow(1.0 - tt, 1.2) * 0.6 * step(tb, grow);
    }
    else along *= 1.0 - smoothstep(free > 0.5 ? 0.72 : 0.90, 1.0, tt);
    // la pointe qui pousse s'éteint sur 6 % (pas de bout franc sous le bloom)
    if (grow < 1.0) along *= 1.0 - smoothstep(grow - 0.06, grow, tt);
    return along;
  }

  // UN SEGMENT = UNE CAPSULE : les deux bouts du segment i à l'écran, un quad
  // qui les déborde de la largeur du ruban de chaque côté, et (vU, vV) les
  // coordonnées du pixel le long du segment et en travers, en pixels. Le
  // fragment en tire la distance EXACTE au segment : aucun pli de ruban aux
  // coudes de l'éclair (les rayons flous de l'ancienne bande continue).
  void main() {
    int s = int(mod(aLine, ${SLOTS}.0) + 0.5);
    float b = floor(aLine / ${SLOTS}.0 + 0.001);
    vec4 gB = uB[s], gD = uD[s];
    float I = gB.w;
    vBranch = b > 0.5 ? 1.0 : 0.0;
    float i = position.x, e = position.z;
    float grow = gD.y;
    float t0 = min(i / uSegs, grow);
    float t1 = min((i + 1.0) / uSegs, grow);
    // emplacement éteint, branche absente, segment au-delà de la pousse : hors champ
    if (I <= 0.0 || t1 <= t0 + 1e-5 || (b > 0.5 && (b > gD.x + 0.5 || uBranchesOn < 0.5))) {
      gl_Position = vec4(2.0, 2.0, 2.0, 1.0); vU = 0.0; vV = 0.0; vL = 0.0; vI = 0.0; vNdc = vec2(0.0); return;
    }
    vec3 q0 = pointAt(aLine, t0), q1 = pointAt(aLine, t1);
    vec4 r0 = projectionMatrix * viewMatrix * vec4(q0, 1.0);
    vec4 r1 = projectionMatrix * viewMatrix * vec4(q1, 1.0);
    vec4 c0 = pushOut(r0), c1 = pushOut(r1);
    vec2 hres = uRes * 0.5;
    vec2 pa = c0.xy / c0.w * hres, pb = c1.xy / c1.w * hres;
    vec2 d = pb - pa;
    float L = length(d);
    vec2 dir = L > 1e-4 ? d / L : vec2(1.0, 0.0);
    vec2 nrm = vec2(-dir.y, dir.x);
    float halo = uMirror > 0.5 ? ${MIRROR_HALO.toFixed(1)} : (b > 0.5 ? ${BRANCH_HALO.toFixed(1)} : ${TRUNK_HALO.toFixed(1)});
    // le halo : 6 largeurs de halo (R12) ; le coeur et la gaine : la gaine + 1,5 px
    float hw = uHalo > 0.5 || uMirror > 0.5 ? 6.0 * halo : (b > 0.5 ? 3.0 : 4.5);
    // le débord le long du segment : plein aux deux bouts de la ligne ; aux
    // jointures, juste de quoi fermer le coin extérieur d'un coude (à 30° de
    // coude, le halo au-delà de 0,35 hw est déjà sous 1 %) - les quads qui se
    // recouvrent à chaque jointure, c'est le coût de remplissage au Pi
    bool lineEnd = e < 0.5 ? i < 0.5 : i > uSegs - 1.5;
    float ext = lineEnd ? hw : hw * (uHalo > 0.5 || uMirror > 0.5 ? 0.35 : 0.7);
    vec2 P = (e < 0.5 ? pa - dir * ext : pb + dir * ext) + nrm * position.y * hw;
    vec4 cE = e < 0.5 ? c0 : c1;
    gl_Position = vec4(P / hres, cE.z / cE.w, 1.0);
    vU = e < 0.5 ? -ext : L + ext;
    vV = position.y * hw;
    vL = L;
    vNdc = P / hres;
    float tt = (i + e) / uSegs;
    float along = alongAt(aLine, b, min(tt, grow), grow, gD.z);
    // une branche qui s'approche de la pochette s'éteint avant d'y entrer ;
    // le tronc, lui, est tenu à l'écart par le CPU (la poussée n'est qu'un filet)
    float nearCover = b > 0.5 && uMirror < 0.5 ? smoothstep(0.0, 0.06, coverDist(e < 0.5 ? r0 : r1)) : 1.0;
    if (uMirror > 0.5) {
      // le reflet s'éteint avec la hauteur du point au-dessus du sol
      vec3 q = e < 0.5 ? q0 : q1;
      along *= 0.35 * exp(-(${FLOOR_Y.toFixed(3)} - q.y) / 0.35);
      // un ruban de reflet est large : ses deux bouts s'éteignent sur 30 %
      along *= smoothstep(0.0, 0.3, tt) * (1.0 - smoothstep(0.7, 1.0, tt));
    }
    vI = I * along * nearCover;
  }
`;

// highp : les uniforms de la pochette sont partagés avec le vertex shader,
// et WebGL refuse deux précisions différentes pour un même uniform
const ARC_FRAGMENT = `
  precision highp float;
  uniform vec3 uCore;
  uniform vec3 uSheath;
  uniform float uMirror;
  uniform float uHalo;
  uniform float uGain;
  varying float vU;
  varying float vV;
  varying float vL;
  varying float vI;
  varying float vBranch;
  varying vec2 vNdc;
  ${SAFE_CHUNK}
  void main() {
    if (vI <= 0.001) discard;
    float halo = uMirror > 0.5 ? ${MIRROR_HALO.toFixed(1)} : (vBranch > 0.5 ? ${BRANCH_HALO.toFixed(1)} : ${TRUNK_HALO.toFixed(1)});
    float wide = uHalo > 0.5 || uMirror > 0.5 ? 1.0 : 0.0;
    float hw = wide > 0.5 ? 6.0 * halo : (vBranch > 0.5 ? 3.0 : 4.5);
    // la distance au segment, en pixels (capsule)
    float u = clamp(vU, 0.0, vL);
    float d = length(vec2(vU - u, vV));
    if (d >= hw) discard;
    vec3 c;
    if (wide > 0.5) {
      // le halo (ou le reflet flou, gaine large sans coeur) : sur le tracé grossier
      float win = 1.0 - smoothstep(0.55 * hw, hw, d);   // le halo atteint 0 au bord (R12)
      c = uSheath * (0.5 * exp(-d / halo) * win);       // halo : luminance 0,35
      if (uMirror > 0.5) c = mix(c, uSheath, 1.0 - smoothstep(6.25, 7.75, d));
    } else {
      float cw = vBranch > 0.5 ? 0.5 : 1.0;             // demi-coeur : 1 px (tronc), 0,5 (branche)
      float sw = vBranch > 0.5 ? 1.75 : 3.0;            // demi-gaine
      float core = 1.0 - smoothstep(cw - 0.5, cw + 0.5, d);
      float sheath = 1.0 - smoothstep(sw - 0.75, sw + 0.75, d);
      c = mix(uSheath * sheath, uCore, core);
    }
    gl_FragColor = vec4(c * vI * uGain * safeAtt(vNdc), 1.0);
  }
`;

// les codes de matériau (attribut aPart)
export const P_SOCLE = 0, P_ACRYL = 1, P_WHITE = 2, P_COPPER = 3, P_WIND = 4, P_CAP = 5, P_ALU = 6, P_LED_R = 7, P_LED_G = 8;

// LA LUMIÈRE DES ARCS, partagée par les bobines et le sol : 4 sources en
// uniforms SÉPARÉS et une boucle déroulée (un tableau indexé par une
// variable de boucle passe par l'unité de texture sur V3D)
const LIGHT_CHUNK = `
  uniform vec4 uL0;
  uniform vec4 uL1;
  uniform vec4 uL2;
  uniform vec4 uL3;
  uniform vec3 uLCol;
  float gD, gS;
  void arcLight(vec4 L, vec3 p, vec3 n, vec3 r, float shin) {
    vec3 d = L.xyz - p;
    float dd = dot(d, d);
    vec3 l = d * inversesqrt(dd + 1e-6);
    float att = L.w / (1.0 + dd * ${(1 / (LIGHT_D * LIGHT_D)).toFixed(3)});
    gD += max(dot(n, l), 0.0) * att;
    gS += pow(max(dot(r, l), 0.0), shin) * att;
  }
  void arcLights(vec3 p, vec3 n, vec3 r, float shin) {
    gD = 0.0; gS = 0.0;
    arcLight(uL0, p, n, r, shin);
    arcLight(uL1, p, n, r, shin);
    arcLight(uL2, p, n, r, shin);
    arcLight(uL3, p, n, r, shin);
  }
`;

const COIL_VERTEX = `
  attribute float aCoil;
  attribute float aPart;
  uniform vec3 uOrigL;
  uniform vec3 uOrigR;
  uniform float uH;
  uniform float uMirror;
  varying vec3 vN;
  varying vec3 vW;
  varying vec3 vWN;
  varying vec3 vLoc;
  varying float vCoil;
  varying float vPart;
  varying vec2 vNdc;
  void main() {
    vec3 p = position, n = normal;
    vCoil = aCoil; vPart = aPart;
    vLoc = (p - (aCoil < 0.5 ? uOrigL : uOrigR)) / uH;
    // le reflet : la bobine retournée sous le plan du sol
    if (uMirror > 0.5) { p.y = ${(2 * FLOOR_Y).toFixed(3)} - p.y; n.y = -n.y; }
    vW = p; vWN = n;
    vN = normalize(normalMatrix * n);
    vec4 c = projectionMatrix * viewMatrix * vec4(p, 1.0);
    vNdc = c.xy / c.w;
    gl_Position = c;
  }
`;
const COIL_FRAGMENT = `
  precision highp float;
  uniform sampler2D uMat;
  uniform vec2 uLedL;        // rouge, vert
  uniform vec2 uLedR;
  uniform float uMirror;
  uniform float uGain;
  uniform float uParts;      // ?teslaparts=k : la pièce k - 1 seule, en blanc (sonde)
  varying vec3 vN;
  varying vec3 vW;
  varying vec3 vWN;
  varying vec3 vLoc;
  varying float vCoil;
  varying float vPart;
  varying vec2 vNdc;
  ${SAFE_CHUNK}
  ${LIGHT_CHUNK}
  void main() {
    vec3 n = normalize(vWN);
    vec3 nv = normalize(vN);
    float part = vPart;
    if (uParts > 0.5) {
      // la sonde : la pièce uParts - 1 en blanc, tout le reste en noir
      gl_FragColor = vec4(vec3(abs(part - (uParts - 1.0)) < 0.5 && uMirror < 0.5 ? 0.8 : 0.0), 1.0);
      return;
    }
    float groove = 1.0;
    if (part > ${P_WIND - 0.5} && part < ${P_WIND + 0.5}) {
      // le fil émaillé : une spire par période, dans la normale ; trop fin
      // pour l'écran, le relief s'efface (fwidth) et il reste le cuivre lisse
      float x = vLoc.y * ${TURNS_PER_H}.0;
      float fade = 1.0 - smoothstep(0.22, 0.45, fwidth(x));
      float w = sin(6.2831853 * x);
      n = normalize(n + vec3(0.0, 0.6 * w * fade, 0.0));
      nv = normalize(nv + vec3(0.0, 0.6 * w * fade, 0.0));
      groove = 1.0 - 0.45 * fade * smoothstep(0.6, 1.0, abs(cos(3.14159265 * x)));
    }
    vec3 V = normalize(cameraPosition - vW);
    vec3 r = reflect(-V, n);
    bool metal = part > ${P_COPPER - 0.5} && part < ${P_ALU + 0.5};
    arcLights(vW, n, r, metal ? 40.0 : 12.0);
    vec4 m = texture2D(uMat, nv.xy * 0.49 + 0.5);
    float body = m.b * m.b * ${MATCAP_GAIN.toFixed(2)};
    float box = m.a * ${CHROME_BOX.toFixed(2)};
    if (uMirror > 0.5) box *= 0.25;          // le reflet flou : plus de boîtes nettes
    vec3 col;
    if (part < ${P_SOCLE + 0.5}) {
      // le socle : noir mat, à peine visible ; fentes d'aération en façade
      float vent = 1.0;
      if (vWN.z > 0.7 && abs(vLoc.x) < 0.12 && vLoc.y > 0.045 && vLoc.y < 0.107) {
        float u = (vLoc.y - 0.045) / 0.0124;
        float fw = fwidth(u);
        float f = fract(u);
        vent = 1.0 - 0.95 * smoothstep(0.25 - fw, 0.25 + fw, f) * (1.0 - smoothstep(0.75 - fw, 0.75 + fw, f));
      }
      col = vec3(0.012, 0.012, 0.014) * (0.3 + body) * vent + uLCol * (gD * 0.07 + gS * 0.06) * vent;
    } else if (part < ${P_ACRYL + 0.5}) {
      // l'acrylique fumé : un reflet sombre du studio, la lumière des arcs en spéculaire
      col = vec3(0.52, 0.56, 0.68) * (body * 0.10 + box * 0.20) + uLCol * (gD * 0.08 + gS * 0.55);
    } else if (part < ${P_WHITE + 0.5}) {
      // l'isolant blanc : mat, presque invisible au repos
      col = vec3(0.85, 0.85, 0.82) * (0.030 + body * 0.06 + gD * 0.40) + uLCol * gS * 0.15;
    } else if (part < ${P_WIND + 0.5}) {
      // le cuivre : matcap chrome teinté, ombres brun-rouge, reflets chauds
      vec3 cu = vec3(0.98, 0.50, 0.24);
      col = (vec3(0.060, 0.012, 0.007) + cu * body) * ${COPPER_REST.toFixed(2)} + vec3(1.0, 0.78, 0.55) * box * ${(COPPER_REST * 0.85).toFixed(3)}
          + uLCol * cu * gD * 0.8 + mix(uLCol, uLCol * cu, 0.4) * gS * 1.0;
      col *= groove;
    } else if (part < ${P_CAP + 0.5}) {
      // les embouts : plastique noir brillant
      col = vec3(0.55, 0.58, 0.66) * (body * 0.06 + box * 0.10) + uLCol * (gD * 0.06 + gS * 0.50);
    } else if (part < ${P_ALU + 0.5}) {
      // l'aluminium poli : le studio du matcap, et la décharge qui s'y mire
      col = vec3(0.93, 0.96, 1.0) * (body + box) * ${ALU_REST.toFixed(2)} + uLCol * (gD * 0.40 + gS * 1.5);
    } else {
      // les voyants : une lentille, plus chaude au centre
      vec2 led = vCoil < 0.5 ? uLedL : uLedR;
      float on = part < ${P_LED_R + 0.5} ? led.x : led.y;
      vec3 lc = part < ${P_LED_R + 0.5} ? vec3(1.0, 0.07, 0.04) : vec3(0.10, 1.0, 0.25);
      col = lc * (0.06 + 0.85 * on) * (0.55 + 0.45 * nv.z);
    }
    // le reflet s'assombrit et s'éteint en s'éloignant du sol
    if (uMirror > 0.5) col *= 0.30 * exp(-(${FLOOR_Y.toFixed(3)} - vW.y) / 0.22);
    gl_FragColor = vec4(col * uGain * safeAtt(vNdc), 1.0);
  }
`;

const FLOOR_VERTEX = `
  varying vec3 vW;
  varying vec2 vNdc;
  void main() {
    vec4 w = modelMatrix * vec4(position, 1.0);
    vW = w.xyz;
    vec4 c = projectionMatrix * viewMatrix * w;
    vNdc = c.xy / c.w;
    gl_Position = c;
  }
`;
// le sol noir brillant : le lobe flou de chaque source d'arc, autour de la
// direction de reflet du regard (rugosité large : le reflet net des sources
// tomberait hors de l'écran), et un peu de diffus
const FLOOR_FRAGMENT = `
  precision highp float;
  uniform float uGain;
  varying vec3 vW;
  varying vec2 vNdc;
  ${SAFE_CHUNK}
  ${LIGHT_CHUNK}
  void main() {
    vec3 e = normalize(vW - cameraPosition);
    vec3 r = vec3(e.x, -e.y, e.z);
    arcLights(vW, vec3(0.0, 1.0, 0.0), r, 6.0);
    vec3 c = uLCol * (gS * 0.10 + gD * 0.02);
    gl_FragColor = vec4(c * uGain * safeAtt(vNdc), 1.0);
  }
`;

const FLASH_FRAGMENT = `
  precision mediump float;
  uniform vec3 uFlash;
  varying vec2 vUv;
  void main() { gl_FragColor = vec4(uFlash, 1.0); }
`;
const FLASH_VERTEX = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;

// ------------------------------------------------------------- la géométrie

// LES TRACÉS, CALCULÉS PAR LE CPU à chaque crépitement (1/30 s) et rangés
// dans une texture flottante de 64 points x 80 lignes : le même calcul dans
// le vertex shader (déplacement de point médian répété pour chaque bout de
// segment et chaque racine de branche) coûtait 5,5 s de compilation au Pi.
const fract = (x) => x - Math.floor(x);
function h13(x, y, z) {
  x = fract(x * 0.1031); y = fract(y * 0.1030); z = fract(z * 0.0973);
  const d = x * (y + 33.33) + y * (x + 33.33) + z * (z + 33.33);
  x += d; y += d; z += d;
  return fract((x + y) * z);
}
// le déplacement de point médian, fermé : la somme, niveau par niveau, de
// l'interpolation linéaire des décalages des noeuds impairs du niveau
function md(t, seed, amp, out) {
  let sx = 0, sy = 0, a = amp;
  for (let l = 1; l <= 6; l++) {
    const x = t * (1 << l), m = Math.floor(x), f = x - m;
    // un seul des deux noeuds encadrants est impair (les pairs ne bougent pas)
    const k = (m & 1) ? m : m + 1, w = (m & 1) ? 1 - f : f;
    sx += (h13(seed, l, k) * 2 - 1) * w * a;
    sy += (h13(seed + 17, l, k) * 2 - 1) * w * a;
    a *= AMP_DECAY;
  }
  out[0] = sx; out[1] = sy;
  return out;
}
const _d = [0, 0];
// le point déplacé : dans le plan de l'écran (perpendiculaire à la tangente)
// et, plus faible, en profondeur
function disp(bx, by, bz, tx, ty, tz, d, out, o) {
  tx += 1e-6;
  let l = 1 / Math.sqrt(tx * tx + ty * ty + tz * tz); tx *= l; ty *= l; tz *= l;
  let nx = -ty, ny = tx + 1e-6; l = 1 / Math.sqrt(nx * nx + ny * ny); nx *= l; ny *= l;
  // N2 = T x N1
  let mx = ty * 0 - tz * ny, my = tz * nx - tx * 0, mz = tx * ny - ty * nx;
  l = Math.sqrt(mx * mx + my * my + mz * mz) || 1; mx /= l; my /= l; mz /= l;
  out[o] = bx + nx * d[0] + mx * d[1] * 0.6;
  out[o + 1] = by + ny * d[0] + my * d[1] * 0.6;
  out[o + 2] = bz + mz * d[1] * 0.6;
}
const _p = new Float32Array(4);
function trunkPoint(sl, t, out, o) {
  const u = 1 - t, p0 = sl.p0, c = sl.ctrl, p2 = sl.p2;
  disp(u * u * p0[0] + 2 * u * t * c[0] + t * t * p2[0], u * u * p0[1] + 2 * u * t * c[1] + t * t * p2[1], u * u * p0[2] + 2 * u * t * c[2] + t * t * p2[2],
    2 * u * (c[0] - p0[0]) + 2 * t * (p2[0] - c[0]), 2 * u * (c[1] - p0[1]) + 2 * t * (p2[1] - c[1]), 2 * u * (c[2] - p0[2]) + 2 * t * (p2[2] - c[2]),
    md(t, sl.seed, sl.amp, _d), out, o);
}
// une ligne d'emplacement (b = 0 : le tronc ; b = 1..4 : une branche,
// partie du tronc en tb, déviée de 0,45 à 0,95 rad, longue de 18 à 35 % de l'arc)
export function writePath(sl, b, data, row) {
  const o0 = row * POINTS * 4;
  if (b === 0) {
    for (let k = 0; k < POINTS; k++) { trunkPoint(sl, k / (POINTS - 1), data, o0 + k * 4); data[o0 + k * 4 + 3] = 0; }
    return;
  }
  const seed = sl.seed, tb = 0.15 + 0.60 * h13(seed, b, 91);
  trunkPoint(sl, tb, _p, 0); const sx = _p[0], sy = _p[1], sz = _p[2];
  trunkPoint(sl, tb + 1 / 64, _p, 0);
  let dx = _p[0] - sx + 1e-6, dy = _p[1] - sy, dz = _p[2] - sz; const l = Math.hypot(dx, dy, dz); dx /= l; dy /= l; dz /= l;
  const ang = (0.45 + 0.50 * h13(seed, b, 92)) * (h13(seed, b, 93) < 0.5 ? -1 : 1), cs = Math.cos(ang), sn = Math.sin(ang);
  const bx = dx * cs - dy * sn, by = dx * sn + dy * cs, bz = dz;
  const len = sl.len * (0.18 + 0.17 * h13(seed, b, 94));
  for (let k = 0; k < POINTS; k++) {
    const t = k / (POINTS - 1);
    disp(sx + bx * len * t, sy + by * len * t, sz + bz * len * t, bx, by, bz, md(t, seed + 31 * b, len * 0.12, _d), data, o0 + k * 4);
    data[o0 + k * 4 + 3] = tb;
  }
}

function arcGeometry(SEG) {
  const pos = new Float32Array(SEG * 4 * 3), idx = [];
  for (let i = 0; i < SEG; i++) {
    [[0, -1], [0, 1], [1, -1], [1, 1]].forEach(([e, side], k) => { const o = (i * 4 + k) * 3; pos[o] = i; pos[o + 1] = side; pos[o + 2] = e; });
    const a = i * 4;
    idx.push(a, a + 2, a + 1, a + 1, a + 2, a + 3);
  }
  const g = new THREE.InstancedBufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setIndex(idx);
  const lines = new Float32Array(LINES);
  for (let k = 0; k < LINES; k++) lines[k] = k;
  g.setAttribute('aLine', new THREE.InstancedBufferAttribute(lines, 1));
  g.instanceCount = LINES;
  g.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e3);
  return g;
}

// LA BOÎTE ARRONDIE à normales exactes : une grille sur chaque face du
// cube, serrée près des arêtes, projetée sur la boîte intérieure + r x n
function roundedBox(w, h, d, r, k) {
  const half = [w / 2, h / 2, d / 2];
  const axisSamples = (hh) => {
    const v = [];
    for (let i = 0; i <= k; i++) v.push(-hh + r * i / k);
    for (let i = k; i >= 0; i--) v.push(hh - r * i / k);
    return v;
  };
  const pos = [], nrm = [], uv = [], idx = [];
  for (let a = 0; a < 3; a++) for (const s of [-1, 1]) {
    const u = (a + 1) % 3, v = (a + 2) % 3;
    const su = axisSamples(half[u]), sv = axisSamples(half[v]);
    const base = pos.length / 3;
    for (let j = 0; j < sv.length; j++) for (let i = 0; i < su.length; i++) {
      const P = [0, 0, 0]; P[a] = s * half[a]; P[u] = su[i]; P[v] = sv[j];
      const q = P.map((c, t) => Math.max(-half[t] + r, Math.min(half[t] - r, c)));
      let n = [P[0] - q[0], P[1] - q[1], P[2] - q[2]];
      const l = Math.hypot(n[0], n[1], n[2]) || 1;
      n = n.map((c) => c / l);
      pos.push(q[0] + n[0] * r, q[1] + n[1] * r, q[2] + n[2] * r);
      nrm.push(n[0], n[1], n[2]);
      uv.push(i / (su.length - 1), j / (sv.length - 1));
    }
    const nu = su.length;
    for (let j = 0; j < sv.length - 1; j++) for (let i = 0; i < nu - 1; i++) {
      const p0 = base + j * nu + i, p1 = p0 + 1, p2 = p0 + nu, p3 = p2 + 1;
      // l'enroulement : la normale de la face vers l'extérieur
      if (s > 0) idx.push(p0, p1, p3, p0, p3, p2); else idx.push(p0, p3, p1, p0, p2, p3);
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(nrm, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  g.setIndex(idx);
  return g;
}

// un dôme : calotte d'ellipsoïde (rayon R, hauteur hgt), normales exactes ;
// up = -1 le retourne vers le bas
function dome(R, hgt, up, seg) {
  const g = new THREE.SphereGeometry(1, seg, 8, 0, Math.PI * 2, 0, Math.PI / 2);
  const p = g.getAttribute('position'), n = g.getAttribute('normal');
  for (let i = 0; i < p.count; i++) {
    const x = p.getX(i), y = p.getY(i), z = p.getZ(i);
    p.setXYZ(i, x * R, y * hgt * up, z * R);
    const nx = x / R, ny = y * up / hgt, nz = z / R, l = Math.hypot(nx, ny, nz) || 1;
    n.setXYZ(i, nx / l, ny / l, nz / l);
  }
  if (up < 0) { const ix = g.index.array; for (let i = 0; i < ix.length; i += 3) { const t = ix[i + 1]; ix[i + 1] = ix[i + 2]; ix[i + 2] = t; } }
  return g;
}

// le primaire : spirale d'Archimède conique, montée de 20° vers l'extérieur
class PrimarySpiral extends THREE.Curve {
  getPoint(t, out) {
    const c = COIL, o = out || new THREE.Vector3();
    const th = 2 * Math.PI * c.spiralTurns * t;
    const r = c.spiralR0 + (c.spiralR1 - c.spiralR0) * t;
    return o.set(r * Math.cos(th), c.spiralY0 + (r - c.spiralR0) * Math.tan(c.spiralRise * Math.PI / 180), r * Math.sin(th));
  }
}

// UNE BOBINE, en fractions de H, origine au sol sous son axe ; double = le
// petit tore empilé (la droite)
function coilParts(double, tier) {
  const c = COIL, T = TIER[tier], out = [];
  const add = (g, part) => out.push([g, part]);
  const cyl = (r, y0, y1, seg, part, open) => {
    const g = new THREE.CylinderGeometry(r, r, y1 - y0, seg, 1, !!open);
    g.translate(0, (y0 + y1) / 2, 0); add(g, part);
  };
  // le socle : coffret, pieds, voyants
  const box = roundedBox(c.boxW, c.boxTop - c.footH, c.boxD, c.boxR, T.round);
  box.translate(0, (c.footH + c.boxTop) / 2, 0); add(box, P_SOCLE);
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) {
    const g = new THREE.CylinderGeometry(c.footR, c.footR, c.footH, 12, 1);
    g.translate(sx * (c.boxW / 2 - 0.04), c.footH / 2, sz * (c.boxD / 2 - 0.04)); add(g, P_SOCLE);
  }
  [[P_LED_R, 0.150], [P_LED_G, 0.180]].forEach(([part, x]) => {
    const g = new THREE.SphereGeometry(0.0095, 12, 8);
    g.translate(x, 0.085, c.boxD / 2); add(g, part);
  });
  // les entretoises et la plateforme
  for (let k = 0; k < 6; k++) {
    const a = (k + 0.5) / 6 * Math.PI * 2;
    const g = new THREE.CylinderGeometry(c.standW, c.standW, c.platY - c.boxTop, 12, 1, true);
    g.translate(c.standR * Math.cos(a), (c.platY + c.boxTop) / 2, c.standR * Math.sin(a)); add(g, P_WHITE);
  }
  cyl(c.platR, c.platY, c.platY + c.platT, 96, P_ACRYL);
  // le primaire, ses peignes, l'anneau de garde
  const rise = c.spiralRise * Math.PI / 180;
  add(new THREE.TubeGeometry(new PrimarySpiral(), c.spiralTurns * T.spiral, c.spiralTube, 8, false), P_COPPER);
  const rm = (c.spiralR0 + c.spiralR1) / 2, len = (c.spiralR1 - c.spiralR0) / Math.cos(rise) + 0.03;
  for (let k = 0; k < 4; k++) {
    const a = (k + 0.5) / 4 * Math.PI * 2;
    const g = roundedBox(len, 0.016, 0.010, 0.003, 2);
    g.rotateZ(rise); g.rotateY(-a);
    const y = c.spiralY0 + (rm - c.spiralR0) * Math.tan(rise) - c.spiralTube - 0.006;
    g.translate(rm * Math.cos(a), y, rm * Math.sin(a)); add(g, P_WHITE);
  }
  for (let k = 0; k < 4; k++) {
    const a = k / 4 * Math.PI * 2;
    const g = new THREE.CylinderGeometry(0.004, 0.004, c.railY - c.platY - c.platT, 8, 1, true);
    g.translate(c.railR * Math.cos(a), (c.railY + c.platY + c.platT) / 2, c.railR * Math.sin(a)); add(g, P_WHITE);
  }
  const rail = new THREE.TorusGeometry(c.railR, c.railT, 8, 128);
  rail.rotateX(Math.PI / 2); rail.translate(0, c.railY, 0); add(rail, P_COPPER);
  // le secondaire et ses embouts
  cyl(c.secR, c.secY0 + c.capH, c.secY1 - c.capH, 64, P_WIND, true);
  cyl(c.capR, c.secY0, c.secY0 + c.capH, 48, P_CAP);
  cyl(c.capR, c.secY1 - c.capH, c.secY1, 48, P_CAP);
  // le tore, ses dômes, la pointe
  const tor = new THREE.TorusGeometry(c.torR, c.torT, T.torus[0], T.torus[1]);
  tor.rotateX(Math.PI / 2); tor.translate(0, c.torY, 0); add(tor, P_ALU);
  const top = dome(c.torR, c.domeH, 1, 48); top.translate(0, c.torY, 0); add(top, P_ALU);
  const bot = dome(c.torR, c.domeH * 0.5, -1, 48); bot.translate(0, c.torY, 0); add(bot, P_ALU);
  let tipBase = c.torY + c.domeH, tipTop = c.tipTop;
  if (double) {
    cyl(c.neckR, c.torY, c.tor2Y, 32, P_ALU, true);
    const t2 = new THREE.TorusGeometry(c.tor2R, c.tor2T, Math.max(16, T.torus[0] - 8), Math.round(T.torus[1] * 0.75));
    t2.rotateX(Math.PI / 2); t2.translate(0, c.tor2Y, 0); add(t2, P_ALU);
    const d2 = dome(c.tor2R, c.dome2H, 1, 40); d2.translate(0, c.tor2Y, 0); add(d2, P_ALU);
    const b2 = dome(c.tor2R, c.dome2H * 0.5, -1, 40); b2.translate(0, c.tor2Y, 0); add(b2, P_ALU);
    tipBase = c.tor2Y + c.dome2H; tipTop = c.tip2Top;
  }
  cyl(c.tipR, tipBase - 0.005, tipTop, 10, P_ALU, true);
  const ball = new THREE.SphereGeometry(c.ballR, 16, 10); ball.translate(0, tipTop, 0); add(ball, P_ALU);
  return out;
}

// LES DEUX BOBINES en un maillage indexé : chaque pièce, mise à l'échelle
// (H) et posée sur le sol à ±x
export function coilGeometry(x, H, tier) {
  const parts = [];
  [-x, x].forEach((x0, coil) => {
    for (const [g, part] of coilParts(coil === 1, tier)) {
      g.scale(H, H, H); g.translate(x0, FLOOR_Y, 0);
      parts.push([g, part, coil]);
    }
  });
  let nv = 0, ni = 0;
  for (const [g] of parts) { nv += g.getAttribute('position').count; ni += g.index ? g.index.count : g.getAttribute('position').count; }
  const P = new Float32Array(nv * 3), N = new Float32Array(nv * 3), UV = new Float32Array(nv * 2);
  const AC = new Float32Array(nv), AP = new Float32Array(nv), I = new Uint32Array(ni);
  let ov = 0, oi = 0;
  for (const [g, part, coil] of parts) {
    const p = g.getAttribute('position'), n = g.getAttribute('normal'), uv = g.getAttribute('uv');
    P.set(p.array, ov * 3); N.set(n.array, ov * 3);
    if (uv) UV.set(uv.array, ov * 2);
    AC.fill(coil, ov, ov + p.count); AP.fill(part, ov, ov + p.count);
    if (g.index) { const ix = g.index.array; for (let i = 0; i < ix.length; i++) I[oi + i] = ix[i] + ov; oi += ix.length; }
    else { for (let i = 0; i < p.count; i++) I[oi + i] = ov + i; oi += p.count; }
    ov += p.count;
    g.dispose();
  }
  const out = new THREE.BufferGeometry();
  out.setAttribute('position', new THREE.BufferAttribute(P, 3));
  out.setAttribute('normal', new THREE.BufferAttribute(N, 3));
  out.setAttribute('uv', new THREE.BufferAttribute(UV, 2));
  out.setAttribute('aCoil', new THREE.BufferAttribute(AC, 1));
  out.setAttribute('aPart', new THREE.BufferAttribute(AP, 1));
  out.setIndex(new THREE.BufferAttribute(I, 1));
  out.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e3);
  return out;
}

// LA PLACE DES BOBINES : centrées entre la pochette (gonflée) et le bord
// (marge R6) ; si l'écran est trop étroit, elles rapetissent. Renvoie x, H
// et la demi-largeur w (la plateforme).
export function coilLayout(A, sc, sh) {
  const inner = Math.max(sc.x + sh.x, sh.x - sc.x) + COVER_MARGIN;
  const outer = A - FRAME_MARGIN;
  const s = Math.max(0.3, Math.min(1, (outer - inner) / (2 * COIL.platR * H_FULL)));
  const H = H_FULL * s, w = COIL.platR * H;
  const x = Math.max(inner + w, Math.min(outer - w, (inner + outer) / 2));
  return { x, H, w, top: FLOOR_Y + COIL.tip2Top * H + COIL.ballR * H };
}

let MATCAP_TEX = null;
function matcapTexture() {
  if (MATCAP_TEX) return MATCAP_TEX;
  const m = genererCanauxRuban(256);
  const t = new THREE.DataTexture(m.data, m.size, m.size, THREE.RGBAFormat, THREE.UnsignedByteType);
  t.colorSpace = THREE.NoColorSpace; t.minFilter = THREE.LinearFilter; t.magFilter = THREE.LinearFilter;
  t.generateMipmaps = false; t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping; t.needsUpdate = true;
  MATCAP_TEX = t;
  return t;
}


// LES MATÉRIAUX, CRÉÉS UNE FOIS POUR TOUTES : libérer un programme à chaque
// départ de Tesla, c'est le recompiler à chaque retour (plusieurs centaines
// de ms au Pi). Une seule Tesla vit à la fois ; init() remet les valeurs à zéro.
// Les objets d'uniforms sont partagés AVEC les matériaux (three garde l'objet
// d'origine : en substituer un autre figerait le rendu).
let GL = null;
function sharedGL() {
  if (GL) return GL;
  const gain = { value: 1 };
  const safe = { uSafeAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather, uStageCenter: safeZoneUniforms.uStageCenter, uStageHalfSize: safeZoneUniforms.uStageHalfSize };
  const v4 = () => Array.from({ length: SLOTS }, () => new THREE.Vector4());
  // la texture des tracés (64 points x 80 lignes, flottante, lue sans filtrage)
  const pathData = new Float32Array(POINTS * LINES * 4);
  const pathTex = new THREE.DataTexture(pathData, POINTS, LINES, THREE.RGBAFormat, THREE.FloatType);
  pathTex.minFilter = pathTex.magFilter = THREE.NearestFilter;
  pathTex.generateMipmaps = false; pathTex.needsUpdate = true;
  const AU = Object.assign({
    uB: { value: v4() }, uD: { value: v4() }, uPath: { value: pathTex },
    uRes: { value: new THREE.Vector2(1280, 720) }, uBranchesOn: { value: 1 },
    uCore: { value: new THREE.Vector3(CORE[0] * CORE_GAIN, CORE[1] * CORE_GAIN, CORE[2] * CORE_GAIN) },
    uSheath: { value: new THREE.Vector3(1, 0, 1) }, uGain: gain
  }, safe);
  const arcMat = (mirror, halo) => new THREE.ShaderMaterial({
    uniforms: Object.assign({}, AU, { uMirror: { value: mirror ? 1 : 0 }, uHalo: { value: halo ? 1 : 0 }, uSegs: { value: mirror || halo ? HALO_SEGS : POINTS - 1 } }),
    vertexShader: ARC_VERTEX, fragmentShader: ARC_FRAGMENT,
    transparent: true, depthWrite: false, depthTest: !mirror,
    // MAX, pas additif : deux arcs qui se croisent ne s'additionnent pas
    // au-dessus du seuil de bloom (le voile violet sur tout l'écran)
    blending: THREE.CustomBlending, blendEquation: THREE.MaxEquation, blendSrc: THREE.OneFactor, blendDst: THREE.OneFactor
  });
  // la lumière des arcs, partagée par les bobines et le sol
  const LU = {
    uL0: { value: new THREE.Vector4() }, uL1: { value: new THREE.Vector4() },
    uL2: { value: new THREE.Vector4() }, uL3: { value: new THREE.Vector4() },
    uLCol: { value: new THREE.Vector3(1, 0, 1) }
  };
  // les bobines, et leur reflet dans le sol (même géométrie, retournée)
  const CU = Object.assign({
    uMat: { value: matcapTexture() }, uOrigL: { value: new THREE.Vector3() }, uOrigR: { value: new THREE.Vector3() }, uH: { value: 1 },
    uLedL: { value: new THREE.Vector2() }, uLedR: { value: new THREE.Vector2() }, uGain: gain,
    uParts: { value: 0 }
  }, LU, safe);
  const coilMat = (mirror) => new THREE.ShaderMaterial({
    uniforms: Object.assign({}, CU, { uMirror: { value: mirror ? 1 : 0 } }),
    vertexShader: COIL_VERTEX, fragmentShader: COIL_FRAGMENT,
    side: mirror ? THREE.BackSide : THREE.FrontSide            // le miroir retourne l'enroulement
  });
  const FU = Object.assign({ uGain: gain }, LU, safe);
  const flashU = { uFlash: { value: new THREE.Vector3() } };
  GL = {
    gain, AU, LU, CU, FU, flashU, pathData, pathTex,
    arcMat: arcMat(false, false), haloMat: arcMat(false, true), mirrorMat: arcMat(true, false),
    coilMat: coilMat(false), coilMirrorMat: coilMat(true),
    floorMat: new THREE.ShaderMaterial({ uniforms: FU, vertexShader: FLOOR_VERTEX, fragmentShader: FLOOR_FRAGMENT, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending }),
    flashMat: new THREE.ShaderMaterial({ uniforms: flashU, vertexShader: FLASH_VERTEX, fragmentShader: FLASH_FRAGMENT, transparent: true, depthTest: false, depthWrite: false, blending: THREE.AdditiveBlending })
  };
  return GL;
}

// =============================================================================

export class TeslaVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : 2;
    this._tier = TIER[tier] ? tier : 2;
    this._layout = TIER[this._tier];
    const k = urlParam('teslaarc');
    this._forceKind = k === 'free' || k === 'ground' || k === 'bridge' ? k : null;
    this._freeze = urlParam('teslafreeze') === '1';
    const lv = parseFloat(urlParam('teslalevel'));
    this._forceLevel = Number.isFinite(lv) ? clamp01(lv) : (this._freeze ? 0.8 : null);
    this._post = new VdjPost({ bloomStrength: 0.32, bloomThreshold: 0.78, grain: 0.015, vignette: 0.4 });
    this._comfort = new VdjComfort();
  }

  get postProcessing() { return this._post.value; }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    this._prevBackground = scene.background;
    scene.background = new THREE.Color(0x000000);
    this._group = new THREE.Group();
    scene.add(this._group);
    this._aspect = safeZoneUniforms.uAspect.value || camera.aspect || 16 / 9;
    this._placeCamera();

    const G = sharedGL();
    this._gain = G.gain; this._gain.value = 1;
    this._AU = G.AU; this._LU = G.LU; this._CU = G.CU; this._FU = G.FU; this._flashU = G.flashU;
    this._pathData = G.pathData; this._pathTex = G.pathTex;
    this._pathData.fill(0); this._pathTex.needsUpdate = true;
    for (const v of this._AU.uB.value) v.set(0, 0, 0, 0);
    this._CU.uParts.value = parseInt(urlParam('teslaparts'), 10) || 0;
    // trois couches, MAX entre elles : coeur et gaine nets sur le tracé fin
    // (quads étroits), halo et reflet sur un tracé grossier (quads larges mais
    // peu nombreux - des quads larges sur 63 segments coûtaient 180 ms au Pi)
    this._arcGeo = arcGeometry(POINTS - 1);
    this._haloGeo = arcGeometry(HALO_SEGS);
    this._arcGeo.instanceCount = SLOTS * (1 + this._layout.branches);   // les branches au-delà du tier : jamais dessinées
    // le halo large et le reflet : les troncs seulement (les 16 premières
    // lignes) ; une branche garde coeur et gaine - le halo des 60 branches
    // coûtait 10 ms au Pi
    this._haloGeo.instanceCount = SLOTS;
    this._arcMat = G.arcMat; this._haloMat = G.haloMat; this._mirrorMat = G.mirrorMat;
    this._arcs = new THREE.Mesh(this._arcGeo, this._arcMat);
    this._arcs.frustumCulled = false; this._arcs.renderOrder = 3;
    // la largeur des traits est en pixels de la cible où l'on dessine
    const res = this._AU.uRes.value;
    const sizeOf = (renderer) => {
      const t = renderer.getRenderTarget();
      if (t) res.set(t.width, t.height); else renderer.getDrawingBufferSize(res);
    };
    this._arcs.onBeforeRender = sizeOf;
    this._halo = new THREE.Mesh(this._haloGeo, this._haloMat);
    this._halo.frustumCulled = false; this._halo.renderOrder = 2;
    this._halo.onBeforeRender = sizeOf;
    this._mirror = new THREE.Mesh(this._haloGeo, this._mirrorMat);
    this._mirror.frustumCulled = false; this._mirror.renderOrder = 1;
    this._mirror.onBeforeRender = sizeOf;
    this._group.add(this._mirror, this._halo, this._arcs);

    this._coilMat = G.coilMat; this._coilMirrorMat = G.coilMirrorMat;
    this._buildCoils();
    this._coils = new THREE.Mesh(this._coilGeo, this._coilMat);
    this._coils.frustumCulled = false; this._coils.renderOrder = 0;
    this._coilMirror = new THREE.Mesh(this._coilGeo, this._coilMirrorMat);
    this._coilMirror.frustumCulled = false; this._coilMirror.renderOrder = -2;
    this._group.add(this._coilMirror, this._coils);

    // le sol : additif, PAR-DESSUS le reflet des bobines (qui est sous lui)
    this._floorGeo = new THREE.PlaneGeometry(12, 10);
    this._floorGeo.rotateX(-Math.PI / 2);
    this._floorGeo.translate(0, FLOOR_Y, -2);
    this._floorMat = G.floorMat;
    this._floor = new THREE.Mesh(this._floorGeo, this._floorMat);
    this._floor.frustumCulled = false; this._floor.renderOrder = -1;
    this._group.add(this._floor);

    // le flash du drop : un quad plein écran additif
    this._flashGeo = new THREE.PlaneGeometry(1, 1);
    this._flashMat = G.flashMat;
    this._flashMesh = new THREE.Mesh(this._flashGeo, this._flashMat);
    this._flashMesh.frustumCulled = false; this._flashMesh.renderOrder = 10; this._flashMesh.visible = false;
    this._group.add(this._flashMesh);

    // l'état
    this._rnd = rng(7331);
    this._time = 0; this._paused = false;
    this._slots = [];
    for (let s = 0; s < SLOTS; s++) this._slots.push({ active: false, coil: s < 6 ? 0 : (s < 12 ? 1 : -1), kind: 'free', seed: 1 + s * 37, dir: [0, 1, 0], p0: [0, 0, 0], p2: [0, 0, 0], ctrl: [0, 0, 0], amp: 0, len: 0, branches: 2, born: 0, intensity: 0, flick: 1, written: -1, until: 0 });
    this._env = [0, 0];
    this._act = [0, 0];
    this._glow = [0, 0];
    this._prevBand = [0, 0];
    this._phaseBeat = null; this._phaseBar = null; this._phasePhrase = null; this._free = 0;
    this._beats = 0; this._bars = 0; this._phrases = 0; this._halfFired = false; this._sinceBeat = 10;
    this._secondsBeat = 0.5;
    this._buildup = 0;
    this._reseedClock = 0;
    this._bridgeUntil = -1; this._dropHoldUntil = -1; this._sheafUntil = -1;
    this._flash = 0;
    this._colIdx = 0;
    this._paletteKey = null;
    this._marksRef = undefined; this._drops = []; this._dropIdx = 0; this._lastNowMs = null; this._lastMode = null; this._lastDropAt = -1e9;
    this._stats = { beat: 0, off: 0, bar: 0, drop: 0, flashes: 0, pushes: 0, rejected: 0 };
    this._applyPalette(null);
    if (this._forceLevel != null) { this._env = [this._forceLevel, this._forceLevel]; this._act = [this._forceLevel, this._forceLevel]; }
    this._updateSlots(0, true);
    this._writeUniforms();
  }

  // la place et la taille des bobines (R6), puis leur maillage
  _buildCoils() {
    const L = coilLayout(this._aspect, safeZoneUniforms.uStageCenter.value, safeZoneUniforms.uStageHalfSize.value);   // R19 : depuis le centre de scène
    this._coilX = L.x; this._H = L.H; this._layoutKey = [L.x, L.H].join();
    if (this._coilGeo) this._coilGeo.dispose();
    this._coilGeo = coilGeometry(L.x, L.H, this._tier);
    this._CU.uOrigL.value.set(-L.x, FLOOR_Y, 0); this._CU.uOrigR.value.set(L.x, FLOOR_Y, 0);
    this._CU.uH.value = L.H;
    if (this._coils) { this._coils.geometry = this._coilGeo; this._coilMirror.geometry = this._coilGeo; }
  }
  // la pose ENTIÈRE de la caméra, à chaque image (la base de dérive du
  // gestionnaire de scène ne doit rien ajouter)
  _placeCamera() {
    const cam = this._camera;
    cam.fov = THREE.MathUtils.radToDeg(2 * Math.atan(1 / CAM_D));
    cam.near = 0.05; cam.far = 40;
    cam.position.set(0, 0, CAM_D);
    cam.rotation.set(0, 0, 0);
    cam.up.set(0, 1, 0);
    cam.updateProjectionMatrix();
    cam.updateMatrixWorld();
  }

  _applyPalette(pal) {
    const v = pal && pal.vividColors && pal.vividColors.length ? pal.vividColors : [FALLBACK_ACCENT];
    const key = v.map(String).join('|');
    if (key === this._paletteKey) return;
    this._paletteKey = key;
    this._accent = vdjColor(v[0]);
    this._setColour();
  }
  // PHRASE : violet, bleu glacier, accent de la pochette
  _colour() {
    const k = this._colIdx % 3;
    return k === 0 ? VIOLET : (k === 1 ? GLACIER : this._accent);
  }
  _setColour() {
    const c = this._colour().clone();
    const l = Math.max(lum(c), 1e-3);
    c.multiplyScalar(SHEATH_LUM / l);                 // gaine à luminance 0,7 (R1)
    this._AU.uSheath.value.set(c.r, c.g, c.b);
    this._sheathCol = c;
  }

  // ----------------------------------------------------------- les arcs (CPU)

  // l'origine d'un arc : la pointe (45 %), sinon un point du tore, de son
  // équateur extérieur à son dessus, plutôt face à la caméra ; la droite
  // tire aussi de son petit tore. towardX : le côté imposé (ponts, sol).
  _origin(coil, towardX) {
    const c = COIL, H = this._H, r = this._rnd;
    const x0 = coil === 0 ? -this._coilX : this._coilX, dbl = coil === 1;
    if (towardX == null && r() < 0.45) return [x0, FLOOR_Y + ((dbl ? c.tip2Top : c.tipTop) + c.ballR) * H, 0];
    const small = dbl && r() < 0.35;
    const R = (small ? c.tor2R : c.torR) * H, T = (small ? c.tor2T : c.torT) * H, Y = FLOOR_Y + (small ? c.tor2Y : c.torY) * H;
    const phi = towardX != null ? (towardX > x0 ? 0 : Math.PI) + (r() - 0.5) * 0.9 : r() * Math.PI * 2;
    const th = towardX != null ? (r() - 0.3) * 0.6 : r() * 1.4;
    const rr = R + T * Math.cos(th);
    const sz = Math.sin(phi);
    return [x0 + rr * Math.cos(phi), Y + T * Math.sin(th), rr * (sz < -0.3 ? -sz : sz)];
  }
  // la courbe de base (Bézier) et sa marge : aucun de ses points, gonflé du
  // déplacement maximal, ne touche la pochette ni une zone d'interface
  _clear(p0, c, p2, margin) {
    const A = this._aspect, sc = safeZoneUniforms.uStageCenter.value, sh = safeZoneUniforms.uStageHalfSize.value;   // R19
    const zones = [[A - 0.22 * A, 1 - 0.12, 0.22 * A, 0.12], [-A + 0.08 * A, 1 - 0.12, 0.08 * A, 0.12], [A - 0.22 * A, -1 + 0.10, 0.22 * A, 0.10]];
    for (let k = 0; k <= 24; k++) {
      const t = k / 24, u = 1 - t;
      const x = u * u * p0[0] + 2 * u * t * c[0] + t * t * p2[0];
      const y = u * u * p0[1] + 2 * u * t * c[1] + t * t * p2[1];
      // le déplacement est nul aux deux bouts : la borne au point t
      const m = mdBoundAt(t) * margin;
      if (Math.abs(x - sc.x) < sh.x + COVER_MARGIN + m && Math.abs(y - sc.y) < sh.y + COVER_MARGIN + m) return false;
      for (const z of zones) if (Math.abs(x - z[0]) < z[2] + ZONE_MARGIN + m && Math.abs(y - z[1]) < z[3] + ZONE_MARGIN + m) return false;
      if (Math.abs(x) > A - 0.04 || y > 0.97) return false;
    }
    return true;
  }
  _spawn(s, kind, opts) {
    const sl = this._slots[s], o = opts || {};
    const r = this._rnd;
    sl.kind = kind; sl.active = true; sl.born = this._time;
    sl.branches = Math.min(this._layout.branches, 2 + Math.floor(r() * 3));
    const level = o.level != null ? o.level : this._env[Math.max(sl.coil, 0)];
    const lenScale = (0.75 + 0.25 * level) * (1 + 0.3 * this._buildup);
    const torOut = (COIL.torR + COIL.torT) * this._H;          // le rayon extérieur du tore
    for (let tries = 0; tries < 12; tries++) {
      if (kind === 'bridge') {
        const p0 = this._origin(0, 0), p2 = this._origin(1, 0);
        const sc = safeZoneUniforms.uStageCenter.value, sh = safeZoneUniforms.uStageHalfSize.value;   // R19
        const apex = Math.min(0.94, Math.max(0.78, sc.y + sh.y + COVER_MARGIN + 0.16)) + (r() - 0.5) * 0.05;
        const cy = 2 * apex - 0.5 * (p0[1] + p2[1]);
        sl.p0 = p0; sl.p2 = p2; sl.ctrl = [(r() - 0.5) * 0.5, cy + (r() - 0.3) * 0.12, (r() - 0.5) * 0.8];
        sl.amp = 0.040; sl.len = 2 * this._coilX;
        return;
      }
      const coil = sl.coil, x0 = coil === 0 ? -this._coilX : this._coilX, out = coil === 0 ? -1 : 1;
      let p0, p2, len, ctrl;
      if (kind === 'ground') {
        // vers l'anneau de garde (le vrai point de chute d'une bobine) ou le sol, côté extérieur
        p0 = this._origin(coil, x0 + out);
        if (r() < 0.5) {
          const psi = (out > 0 ? 0 : Math.PI) + (r() - 0.5) * 1.2;
          p2 = [x0 + COIL.railR * this._H * Math.cos(psi), FLOOR_Y + COIL.railY * this._H, Math.abs(COIL.railR * this._H * Math.sin(psi))];
        } else {
          const dx = out * (COIL.platR * this._H + 0.05 + (0.10 + 0.30 * r()) * lenScale);
          p2 = [x0 + dx, FLOOR_Y, (r() - 0.3) * 0.5];
        }
        len = Math.hypot(p2[0] - p0[0], p2[1] - p0[1]);
        const mid = [(p0[0] + p2[0]) / 2, (p0[1] + p2[1]) / 2, (p0[2] + p2[2]) / 2];
        ctrl = [mid[0] + out * (0.06 + 0.10 * r()) * this._H / H_FULL, mid[1], mid[2] + 0.05];
      } else {
        // libre : vers le haut, les côtés, le vide ; un essai refusé (pochette,
        // zone d'interface, bord) repart plus court et plus redressé
        p0 = this._origin(coil, null);
        const side = Math.abs(p0[0] - x0) > 0.02 && r() < 0.8 ? Math.sign(p0[0] - x0) : (r() < 0.7 ? out : -out);
        const ang0 = o.angle != null ? o.angle : side * (-0.10 + 1.50 * r());
        const ang = ang0 * Math.pow(0.85, tries);
        len = torOut * (1.5 + 1.5 * r()) * lenScale * (o.sheaf ? 1.15 : 1) * Math.pow(0.88, tries);
        const dz = (r() - 0.5) * 0.4;
        p2 = [p0[0] + Math.sin(ang) * len, p0[1] + Math.cos(ang) * len, p0[2] + dz];
        const mid = [(p0[0] + p2[0]) / 2, (p0[1] + p2[1]) / 2, (p0[2] + p2[2]) / 2];
        const bow = (r() - 0.5) * 0.3 * len;
        ctrl = [mid[0] + bow * 0.5, mid[1] + Math.abs(bow) * 0.4, mid[2]];
      }
      const amp = 0.085 * len;
      if (this._clear(p0, ctrl, p2, amp)) { sl.p0 = p0; sl.p2 = p2; sl.ctrl = ctrl; sl.amp = amp; sl.len = len; return; }
      this._stats.rejected++;
      if (tries === 9 && kind === 'free' && !o.sheaf) kind = 'ground';
    }
    sl.active = false;                                  // aucune place : pas d'arc
  }
  _kindFor() {
    if (this._forceKind && this._forceKind !== 'bridge') return this._forceKind;
    return this._rnd() < 0.62 ? 'free' : 'ground';
  }

  // le nombre d'arcs actifs par bobine suit son enveloppe ; nouvelle graine
  // à chaque pas de crépitement ; ponts sur la mesure et au drop
  _updateSlots(dt, reseed) {
    const max = this._layout.maxArcs;
    const sheaf = this._time < this._sheafUntil;
    for (let coil = 0; coil < 2; coil++) {
      const e = this._env[coil];
      const n = sheaf ? max : Math.min(max, Math.ceil(Math.max(e, this._act[coil]) * max - 0.35));
      for (let k = 0; k < 6; k++) {
        const s = coil * 6 + k, sl = this._slots[s];
        const want = k < n && this._forceKind !== 'bridge';
        if (want && !sl.active) this._spawn(s, this._kindFor());
        else if (!want && sl.active) sl.active = false;
        if (sl.active) sl.intensity = sheaf ? 0.9 : clamp01(0.40 + 0.70 * e) * (1 - 0.06 * k);
      }
    }
    // les ponts
    const wantBridges = this._forceKind === 'bridge' ? 2 : (this._time < this._dropHoldUntil ? 3 : (this._time < this._bridgeUntil ? 1 : 0));
    for (let k = 0; k < 4; k++) {
      const s = BRIDGE0 + k, sl = this._slots[s];
      const want = k < wantBridges;
      if (want && !sl.active) this._spawn(s, 'bridge');
      else if (!want && sl.active) sl.active = false;
      if (sl.active) sl.intensity = k === 0 ? 1 : 0.7;
    }
    if (reseed && !this._freeze) {
      for (const sl of this._slots) {
        if (!sl.active) continue;
        sl.seed = 1 + Math.floor(this._rnd() * 997);
        sl.flick = 0.7 + 0.3 * this._rnd();
      }
    }
  }

  _writeUniforms() {
    const U = this._AU;
    for (let s = 0; s < SLOTS; s++) {
      const sl = this._slots[s];
      if (!sl.active) { U.uB.value[s].w = 0; continue; }
      const grow = clamp01((this._time - sl.born) / 0.035);    // l'arc jaillit en 35 ms
      U.uB.value[s].w = sl.intensity;
      U.uD.value[s].set(sl.branches, this._freeze ? 1 : grow, sl.kind === 'free' ? 1 : 0, 0);
      // le tracé : recalculé seulement quand l'arc est né ou a crépité
      const key = sl.seed * 1e4 + sl.born;
      if (key !== sl.written) {
        sl.written = key;
        const nb = Math.min(sl.branches, this._layout.branches);
        for (let b = 0; b <= nb; b++) writePath(sl, b, this._pathData, b * SLOTS + s);
        this._pathTex.needsUpdate = true;
      }
    }
  }

  // ------------------------------------------------------------- le rythme

  _tickDrops(profile) {
    const tl = profile.analysisTimeline;
    const marks = tl && tl.marks ? tl.marks : null;
    if (marks !== this._marksRef) {
      this._marksRef = marks; this._drops = [];
      if (marks) for (const m of marks) if (m.type === 'drop' && m.score >= DROP_SCORE_MIN) this._drops.push(m.atMs);
      this._dropIdx = 0;
    }
    const now = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    if (now == null) return false;
    if (this._lastNowMs != null && (now < this._lastNowMs - 1000 || now > this._lastNowMs + 5000)) this._dropIdx = 0;
    this._lastNowMs = now;
    let fired = false;
    while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) {
      if (now - this._drops[this._dropIdx] < DROP_STALE_MS) fired = true;
      this._dropIdx++;
    }
    return fired;
  }

  // une décharge d'une bobine : l'enveloppe monte, les arcs repartent ; le
  // nombre d'arcs (activité) tient environ un temps
  _discharge(coil, level) {
    this._env[coil] = Math.max(this._env[coil], level);
    this._act[coil] = Math.max(this._act[coil], level);
    for (let k = 0; k < 6; k++) { const sl = this._slots[coil * 6 + k]; if (sl.active) this._spawn(coil * 6 + k, this._kindFor()); }
    this._glow[coil] = Math.max(this._glow[coil], level);
  }

  _onDrop() {
    this._stats.drop++;
    this._sheafUntil = this._time + this._secondsBeat;          // la gerbe : un temps
    this._dropHoldUntil = this._time + DROP_HOLD_BARS * 4 * this._secondsBeat;
    // la gerbe : 6 arcs par bobine, en éventail vers le haut
    const max = this._layout.maxArcs;
    for (let coil = 0; coil < 2; coil++) {
      this._env[coil] = 1;
      const out = coil === 0 ? -1 : 1;
      for (let k = 0; k < 6; k++) {
        const s = coil * 6 + k;
        if (k >= max) { this._slots[s].active = false; continue; }
        const ang = out * (-0.30 + 1.25 * (k + 0.5) / max);
        this._spawn(s, 'free', { angle: ang, level: 1, sheaf: true });
      }
      this._glow[coil] = 1; this._act[coil] = 1;
    }
    if (this._comfort.requestFlash(this._time)) { this._flash = 1; this._stats.flashes++; }
  }

  onTrackChange(profile) { this._applyPalette(profile && profile.artworkPalette); this._dropIdx = 0; this._lastNowMs = null; }

  update(deltaTime, profile) {
    const dt = Math.min(deltaTime, 0.1);
    this._paused = profile.playbackState !== 'PLAYING';
    this._applyPalette(profile.artworkPalette);
    const asp = safeZoneUniforms.uAspect.value;
    if (asp) this._aspect = asp;
    // l'écran ou la pochette ont changé de forme : les bobines se replacent (R19 : autour du centre de scène)
    const L = coilLayout(this._aspect, safeZoneUniforms.uStageCenter.value, safeZoneUniforms.uStageHalfSize.value);
    if (Math.abs(L.x - this._coilX) > 0.01 || Math.abs(L.H - this._H) > 0.01) this._buildCoils();
    this._placeCamera();
    // pause : tout gèle, les arcs restent figés et allumés
    if (this._paused) { this._post.update(1); return; }
    this._time += dt;

    const director = profile.director;
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 0;
    this._secondsBeat = bpm > 0 ? 60 / THREE.MathUtils.clamp(bpm, 60, 200) : 0.5;       // repli sans BPM : 120
    const beatS = this._secondsBeat;
    let beat = false, bar = false, phrase = false, off = false;
    const bp = profile.beatPhase;
    if (bp != null) {
      if (this._phaseBeat != null && bp < this._phaseBeat - 0.5) beat = true;
      if (this._phaseBeat != null && this._phaseBeat < 0.5 && bp >= 0.5) off = true;
      this._phaseBeat = bp;
    } else {
      const prev = this._free;
      this._free += dt;
      if (this._free >= beatS) { this._free -= beatS; beat = true; }
      else if (prev < beatS / 2 && this._free >= beatS / 2) off = true;
    }
    const barPhase = profile.barPhase;
    if (barPhase != null) { if (this._phaseBar != null && barPhase < this._phaseBar - 0.5) bar = true; this._phaseBar = barPhase; }
    else if (beat && (this._beats + 1) % 4 === 0) bar = true;
    const ph = profile.phrasePhase;
    if (ph != null) { if (this._phasePhrase != null && ph < this._phasePhrase - 0.5) phrase = true; this._phasePhrase = ph; }
    else if (beat && (this._beats + 1) % 16 === 0) phrase = true;
    if (beat) { this._beats++; this._sinceBeat = 0; }
    if (bar) this._bars++;
    this._sinceBeat += dt;

    // la montée
    const next = director && director.nextEvent;
    let build = 0;
    if (next && next.type === 'drop' && next.msUntil < 8000) build = 1 - next.msUntil / 8000;
    if (director && director.mode === 'build') build = Math.max(build, 0.6);
    this._buildup += (build - this._buildup) * (1 - Math.exp(-dt / 0.8));

    // les enveloppes : décroissance de 80 ms, attaque instantanée
    const decay = Math.exp(-dt / ENV_DECAY), actDecay = Math.exp(-dt / ACT_DECAY);
    this._env[0] *= decay; this._env[1] *= decay;
    this._act[0] *= actDecay; this._act[1] *= actDecay;
    const bands = director && director.hasReliableGrid && director.bands ? director.bands : null;
    if (bands) {
      // une montée de la bande (nouveau coup) relance l'enveloppe à son niveau
      const vals = [clamp01(bands.low), clamp01(bands.high)];
      for (let c = 0; c < 2; c++) {
        if (vals[c] > this._prevBand[c] + 0.04) this._env[c] = Math.max(this._env[c], vals[c]);
        this._prevBand[c] = vals[c];
      }
    }
    // avec analyse, la force de la décharge suit la bande de sa bobine
    if (beat) { this._stats.beat++; this._discharge(0, bands ? 0.3 + 0.7 * clamp01(bands.low) : 0.85); }
    if (off) { this._stats.off++; this._discharge(1, bands ? 0.3 + 0.7 * clamp01(bands.high) : 0.75); }
    // sans analyse : les aigus aussi en doubles-croches pendant la montée
    if (!bands && this._buildup > 0.3) {
      const q = Math.floor(this._sinceBeat / (beatS / 4));
      if (q !== this._lastSixteenth && q % 2 === 1) this._env[1] = Math.max(this._env[1], 0.35 + 0.3 * this._buildup);
      this._lastSixteenth = q;
    }
    if (this._forceLevel != null) { this._env = [this._forceLevel, this._forceLevel]; this._act = [this._forceLevel, this._forceLevel]; }
    // MESURE : un pont ; PHRASE : couleur suivante
    if (bar) { this._stats.bar++; this._bridgeUntil = this._time + 0.6 * beatS; const sl = this._slots[BRIDGE0]; sl.active = false; }
    if (phrase) { this._phrases++; this._colIdx++; this._setColour(); }
    // DROP
    let drop = this._tickDrops(profile);
    if (director && director.mode === 'drop' && this._lastMode !== 'drop' && director.hasReliableGrid) drop = true;
    this._lastMode = director ? director.mode : null;
    if (drop && this._time - this._lastDropAt > 1) { this._lastDropAt = this._time; this._onDrop(); }

    // le crépitement : une graine toutes les 1/30 s, jusqu'à 3 fois plus vite en montée
    this._reseedClock += dt;
    const step = RESEED / (1 + 2 * this._buildup);
    let reseed = false;
    if (this._reseedClock >= step) { this._reseedClock %= step; reseed = true; }
    this._updateSlots(dt, reseed);
    this._writeUniforms();

    // LA LUMIÈRE DES ARCS : les 4 arcs les plus forts sont 4 sources, au
    // tiers de leur longueur (près de la bobine qu'ils éclairent) ; elles
    // vacillent avec le crépitement
    for (let c = 0; c < 2; c++) this._glow[c] *= Math.exp(-dt / 0.15);
    this._flash *= Math.exp(-dt / 0.12);
    const col = this._sheathCol.clone().multiplyScalar(1 / SHEATH_LUM);
    this._LU.uLCol.value.set(col.r, col.g, col.b);
    const lit = [];
    for (const sl of this._slots) if (sl.active && sl.intensity > 0) lit.push(sl);
    lit.sort((a, b) => b.intensity - a.intensity);
    const LV = [this._LU.uL0.value, this._LU.uL1.value, this._LU.uL2.value, this._LU.uL3.value];
    let lightSum = 0;
    for (let k = 0; k < LIGHTS; k++) {
      const sl = lit[k];
      if (!sl) { LV[k].set(0, 0, 0, 0); continue; }
      const t = sl.kind === 'bridge' ? 0.5 : 0.33, u = 1 - t;
      const grow = this._freeze ? 1 : clamp01((this._time - sl.born) / 0.035);
      const w = sl.intensity * sl.flick * grow * LIGHT_GAIN;
      LV[k].set(
        u * u * sl.p0[0] + 2 * u * t * sl.ctrl[0] + t * t * sl.p2[0],
        u * u * sl.p0[1] + 2 * u * t * sl.ctrl[1] + t * t * sl.p2[1],
        u * u * sl.p0[2] + 2 * u * t * sl.ctrl[2] + t * t * sl.p2[2], w);
      lightSum += w;
    }
    // les voyants : le rouge s'allume à chaque décharge, le vert s'éteint
    this._CU.uLedL.value.set(this._glow[0], 1 - 0.85 * this._glow[0]);
    this._CU.uLedR.value.set(this._glow[1], 1 - 0.85 * this._glow[1]);
    this._flashMesh.visible = this._flash > 0.01;
    this._flashU.uFlash.value.set(col.r, col.g, col.b).multiplyScalar(0.06 * this._flash);
    // R10 : luminance moyenne estimée (bobines au repos, arcs, leur lumière, flash)
    let arcs = 0; for (const sl of this._slots) if (sl.active) arcs += sl.intensity;
    const est = 0.02 + 0.006 * arcs + 0.006 * lightSum + 0.12 * this._flash;
    this._gain.value = this._comfort.measure(est, dt);
    this._post.update(vdjBeatChroma(bp != null ? bp : this._free / beatS, beatS));
  }

  getProbe() {
    return {
      tier: this._tier, env: this._env.map((v) => +v.toFixed(3)), act: this._act.map((v) => +v.toFixed(3)), H: +this._H.toFixed(3),
      active: [this._slots.slice(0, 6).filter((s) => s.active).length, this._slots.slice(6, 12).filter((s) => s.active).length, this._slots.slice(12).filter((s) => s.active).length],
      buildup: +this._buildup.toFixed(2), colour: this._colIdx % 3, stats: Object.assign({}, this._stats), flashes: this._comfort._flashes.length,
      coilX: this._coilX, gain: +this._gain.value.toFixed(3),
      safe: [safeZoneUniforms.uSafeCenter.value.x, safeZoneUniforms.uSafeCenter.value.y, safeZoneUniforms.uSafeHalfSize.value.x, safeZoneUniforms.uSafeHalfSize.value.y, safeZoneUniforms.uAspect.value]
    };
  }

  dispose() {
    if (this._scene) {
      this._scene.remove(this._group);
      this._scene.background = this._prevBackground || null;
    }
    // les matériaux et la texture des tracés restent (sharedGL) : pas de recompilation au retour
    this._arcGeo.dispose(); this._haloGeo.dispose();
    this._coilGeo.dispose();
    this._floorGeo.dispose();
    this._flashGeo.dispose();
    this._scene = null;
  }
}

function rng(seed) { let s = seed >>> 0; return () => { s = (s * 1664525 + 1013904223) >>> 0; return s / 4294967296; }; }
